import {
  ASSIGNABLE_ROLE_KEYS,
  DEFAULT_USER_LIST_SORT,
  HANDOVER_KINDS,
  USER_LIST_SORT_COLUMNS,
  USER_LIST_STATUSES,
  parseUserListSort,
  userListSortToString,
  type DeactivateUserInput,
  type HandoverKind,
  type UserListFilter,
  type UserListSortColumn,
  type UserListStatus,
  type UserSelection,
} from '@nest-recruit/shared';
import type { SortSpec } from '../../../../components/data-table/types';
import { resolveSelection, type SelectionState } from '../../../../lib/selection';
import type { OwnedRecordCounts, UserListParams } from '../../api/users-api';

// USER-01's address (06-qol §3 and §6): the search, the filters, the sort, the page and who is open in the pane,
// so a copied link shows a colleague the same list and the same person. The names are the API's own, so the
// address and the request read alike (`?roleId=…&panel=yes`).

type AssignableRoleKey = (typeof ASSIGNABLE_ROLE_KEYS)[number];

export interface PeopleSearch {
  readonly q?: string;
  readonly status?: UserListStatus;
  /** Any role, a copy included (Roles' "See them"). */
  readonly roleId?: string;
  /** A standard role by its key, for a link written without the role's id. */
  readonly roleKey?: AssignableRoleKey;
  readonly ventureId?: string;
  readonly panel?: 'yes' | 'no';
  readonly twoFactor?: 'on' | 'off';
  /** `column:direction,…`; absent means the default, name A to Z. */
  readonly sort?: string;
  /** Only past the first page. */
  readonly page?: number;
  /** The person open in the pane. */
  readonly person?: string;
  /** The invite form is open in the pane. */
  readonly invite?: true;
}

export const PEOPLE_PAGE_SIZE = 50;

/** Text from the address: TanStack's reader turns `?q=2024` into a number, so a number is read back as text. */
function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  const read = text(value);
  return read !== undefined && (allowed as readonly string[]).includes(read) ? (read as T) : undefined;
}

/** The page's reading of the address. Anything unknown or malformed is dropped, never an error. */
export function readPeopleSearch(raw: Readonly<Record<string, unknown>>): PeopleSearch {
  const next: { -readonly [K in keyof PeopleSearch]: PeopleSearch[K] } = {};
  const q = text(raw.q);
  if (q !== undefined) next.q = q.slice(0, 120);
  const status = oneOf(raw.status, USER_LIST_STATUSES);
  if (status) next.status = status;
  const roleId = text(raw.roleId);
  if (roleId !== undefined) next.roleId = roleId;
  const roleKey = oneOf(raw.roleKey, ASSIGNABLE_ROLE_KEYS);
  if (roleKey) next.roleKey = roleKey;
  const ventureId = text(raw.ventureId);
  if (ventureId !== undefined) next.ventureId = ventureId;
  const panel = oneOf(raw.panel, ['yes', 'no'] as const);
  if (panel) next.panel = panel;
  const twoFactor = oneOf(raw.twoFactor, ['on', 'off'] as const);
  if (twoFactor) next.twoFactor = twoFactor;
  const sort = text(raw.sort);
  if (sort !== undefined) {
    const written = userListSortToString(parseUserListSort(sort));
    if (written !== userListSortToString(DEFAULT_USER_LIST_SORT)) next.sort = written;
  }
  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) next.page = page;
  const person = text(raw.person);
  if (person !== undefined) next.person = person;
  if (raw.invite === true || raw.invite === 'true' || raw.invite === 1 || raw.invite === '1') next.invite = true;
  return next;
}

/** What the list shows and "all matching" acts on: the filters, without the page, the sort or the pane. */
export function filterOf(search: PeopleSearch): UserListFilter {
  return {
    ...(search.q === undefined ? {} : { q: search.q }),
    ...(search.status === undefined ? {} : { status: search.status }),
    ...(search.roleId === undefined ? {} : { roleId: search.roleId }),
    ...(search.roleKey === undefined ? {} : { roleKey: search.roleKey }),
    ...(search.ventureId === undefined ? {} : { ventureId: search.ventureId }),
    ...(search.panel === undefined ? {} : { panel: search.panel }),
    ...(search.twoFactor === undefined ? {} : { twoFactor: search.twoFactor }),
  };
}

/** The tiles count every status the other filters let through, whatever tile is picked. */
export function countsFilterOf(search: PeopleSearch): Omit<UserListFilter, 'status'> {
  return filterOf({ ...search, status: undefined });
}

export function listParamsOf(search: PeopleSearch): UserListParams {
  return {
    ...filterOf(search),
    page: search.page ?? 1,
    pageSize: PEOPLE_PAGE_SIZE,
    sort: search.sort ?? userListSortToString(DEFAULT_USER_LIST_SORT),
  };
}

/** How many filters narrow the list: the tile is one, and so is the search. */
export function filterCount(search: PeopleSearch): number {
  return Object.keys(filterOf(search)).length;
}

/** A selection belongs to the filter it was made under; a new filter starts with nothing ticked. */
export function filterKey(search: PeopleSearch): string {
  return JSON.stringify(filterOf(search));
}

// --- Sort -------------------------------------------------------------------

/** The table's sort marks, from the address: the default shows as name A to Z. */
export function sortOf(search: PeopleSearch): readonly SortSpec[] {
  return parseUserListSort(search.sort).map((spec) => ({ columnId: spec.column, direction: spec.direction }));
}

/** The address's `sort`, from the table's: undefined when it is the default, so a plain list has a plain address. */
export function sortParamOf(specs: readonly SortSpec[]): string | undefined {
  const known = specs.filter((spec): spec is SortSpec & { columnId: UserListSortColumn } =>
    (USER_LIST_SORT_COLUMNS as readonly string[]).includes(spec.columnId),
  );
  if (known.length === 0) return undefined;
  const written = userListSortToString(known.map((spec) => ({ column: spec.columnId, direction: spec.direction })));
  return written === userListSortToString(DEFAULT_USER_LIST_SORT) ? undefined : written;
}

// --- Bulk selection -----------------------------------------------------------

/**
 * What a bulk action acts on: the ticked ids, or "all N matching" as the list's filter less the rows unticked since
 * (06-qol §3, `resolveSelection`). Null when nothing is ticked.
 */
export function toUserSelection(selection: SelectionState, filter: UserListFilter): UserSelection | null {
  if (selection.mode === 'none') return null;
  const resolved = resolveSelection(selection);
  if (resolved.kind === 'all-matching') return { kind: 'all-matching', filter, excluded: [...resolved.excluded] };
  return resolved.ids.length === 0 ? null : { kind: 'ids', ids: [...resolved.ids] };
}

// --- Hand-over --------------------------------------------------------------

/** The kinds of work this person has, in the order the hand-over asks about them. */
export function ownedKinds(owned: OwnedRecordCounts): readonly HandoverKind[] {
  return HANDOVER_KINDS.filter((kind) => owned[kind] > 0);
}

export type HandoverMode = 'one' | 'split';

/**
 * The deactivate request. One person takes everything (the default), or each kind of work goes to the person
 * chosen for it, falling back to the one person where a kind was left on "same as above". Kinds they have none of
 * are never sent.
 */
export function deactivateBody(
  owned: OwnedRecordCounts,
  mode: HandoverMode,
  onePerson: string | null,
  perKind: Partial<Record<HandoverKind, string | null>>,
): DeactivateUserInput {
  const kinds = ownedKinds(owned);
  if (kinds.length === 0) return {};
  if (mode === 'one') return onePerson ? { reassignToUserId: onePerson } : {};
  const handover: Partial<Record<HandoverKind, string>> = {};
  for (const kind of kinds) {
    const chosen = perKind[kind] ?? onePerson;
    if (chosen) handover[kind] = chosen;
  }
  return { handover };
}

/** Whether every kind of work they have has somewhere to go, so the button can be pressed. */
export function isHandoverComplete(
  owned: OwnedRecordCounts,
  mode: HandoverMode,
  onePerson: string | null,
  perKind: Partial<Record<HandoverKind, string | null>>,
): boolean {
  const kinds = ownedKinds(owned);
  if (mode === 'one') return kinds.length === 0 || onePerson !== null;
  return kinds.every((kind) => (perKind[kind] ?? onePerson ?? null) !== null);
}

// --- Invite many --------------------------------------------------------------

/** The addresses typed or pasted, split on commas, semicolons, spaces and new lines; repeats are kept for the server to report. */
export function splitEmails(value: string): readonly string[] {
  return value
    .split(/[\s,;]+/)
    .map((part) => part.replace(/^<|>$/g, '').trim())
    .filter(Boolean);
}
